import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useDebounce } from '@lib/hooks';

import GQL from '@lib/cms/gql';
import { CATEGORY_ATTRIBUTES, ALL_POSTS_QUERY, RELATED_POSTS_QUERY } from '@lib/fragments';

import {
  blog_count,
  search,
  search_bar,
  zero_state,
  spinner
} from '@src/pages/community/styles.module.scss';

import ArticleCard from '@components/article-card';
import Container from '@layout/container';
import Grid from '@layout/grid';
import Jumbotron from '@components/jumbotron';
import Layout from '@layout/index';
import Pagination from '@components/pagination';
import Spinner from '@components/spinner';
import Button from '@components/button';

const PAGE_SIZE = 15;

export async function getStaticPaths() {
  const query = `
  query GetPostBlogCategories {
    postBlogCategoryCollection {
      items {
        slug
      }
    }
  }
  `;
  const {
    data: {
      postBlogCategoryCollection: { items }
    }
  } = await GQL.fetch(query, {});
  const paths = items.map((item) => {
    return {
      params: { slug: item.slug }
    };
  });

  return {
    fallback: false,
    paths
  };
}

export async function getStaticProps(context) {
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment || null;
  const variables = {
    preview,
    previewEnv,
    slug: context.params.slug
  };

  const staticData = await GQL.fetch(
    `
    ${CATEGORY_ATTRIBUTES}
    query GetStaticBlogCategoryData($slug: String!, $preview: Boolean!) {
      postBlogCategoryCollection(where: { slug: $slug } preview: $preview) {
        items {
          ...Category
        }
      }
    }
    `,
    variables,
    preview,
    previewEnv
  );

  if (staticData.errors) {
    throw new Error(staticData.errors[0].message);
  }

  const categoryData = staticData?.data?.postBlogCategoryCollection.items[0];

  // set placeholder Header Image if non has been provided
  categoryData.headerImage = categoryData.headerImage || {
    url: 'https://images.ctfassets.net/wzzep3ntdgx4/3Tl0WO8egFb4oWbuOORUfS/ac2bb05274e8b79df8e9fd9de14edd6b/AdobeStock_137669342.jpeg',
    width: 8033,
    height: 3105
  };

  return {
    props: { categoryData, ...variables }
  };
}

const CategoryLandingPage = (props) => {
  const { preview, previewEnv, categoryData } = props;

  const router = useRouter();
  const topRef = useRef();
  const [pageNum, setPageNum] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [showingAll, setShowingAll] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 250);
  const hasValidInput = debouncedSearch.length >= 3;

  const fetchBlogs = async () => {
    setLoading(true);
    const postData = await GQL.fetch(
      props.slug === 'all' ? ALL_POSTS_QUERY : RELATED_POSTS_QUERY,
      {
        category: [categoryData.categoryId],
        limit: PAGE_SIZE,
        preview,
        searchFilter: searchTerm,
        skip: pageNum * PAGE_SIZE
      },
      preview,
      previewEnv
    );

    setTotal(postData.data.postBlogCollection.total ?? 0);
    setRelatedArticles(postData.data.postBlogCollection.items ?? []);
    setLoading(false);
  };

  useEffect(() => {
    if (!router.isReady) return;
    const { page } = router.query;
    if (page && !isNaN(page)) setPageNum(page - 1);
  }, [router.isReady]);

  useEffect(() => {
    // Don't fetch if it's already showing everything and input isn't valid
    if (!hasValidInput && showingAll) return;
    setShowingAll(!hasValidInput);

    fetchBlogs();
  }, [debouncedSearch]);

  useEffect(() => {
    fetchBlogs();
  }, [pageNum]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setPageNum(0);
    router.push({ pathname: router.pathname, query: { ...router.query, page: '1' } }, undefined, {
      shallow: true
    });
  };

  const handlePagination = ({ page }) => {
    setPageNum(page);
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          page: (page || 0) + 1
        }
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <Layout
      preview={preview}
      meta={{
        description: categoryData.metaDescription || categoryData.description,
        image: categoryData.metaImage
      }}
    >
      <Jumbotron
        image={categoryData.headerImage}
        title={categoryData.title}
        body={categoryData.description}
      />
      {relatedArticles && (
        <>
          <main>
            <Container spacing="large" layout="single">
              <div ref={topRef} className={search_bar}>
                <div className={blog_count}>
                  {total} Post{total === 1 ? '' : 's'}
                </div>
                <div className={search}>
                  <input
                    tabIndex="0"
                    value={searchTerm}
                    onChange={handleInputChange}
                    placeholder="Search by Post Title"
                  />
                </div>
              </div>

              {loading && (
                <div className={spinner}>
                  <Spinner />
                </div>
              )}
              {!loading && relatedArticles.length > 0 ? (
                <Grid gutter="20" layout="3" spacing="none">
                  {relatedArticles.map((post) => {
                    return (
                      <ArticleCard
                        key={post.title}
                        description={post.title}
                        image={post?.featuredImage}
                        link={{
                          aria: 'Explore this post',
                          icon: true,
                          label: post.label,
                          url: `/blog/${post.slug}`
                        }}
                        theme="primary"
                      />
                    );
                  })}
                </Grid>
              ) : (
                <div className={zero_state}>
                  <p>No blogs meet your search criteria.</p>
                  <Button label="Reset Filter" onClick={() => setSearchTerm('')} />
                </div>
              )}
            </Container>
            {!loading && total > PAGE_SIZE && (
              <Container spacing="medium" textAlignment="center" gridOptions={{ layout: 'single' }}>
                <Pagination
                  currentPage={pageNum}
                  items={Array.from(Array(total || 0).keys())}
                  per={PAGE_SIZE}
                  onChange={handlePagination}
                  scrollToPosition={topRef}
                />
              </Container>
            )}
          </main>
        </>
      )}
    </Layout>
  );
};

export default CategoryLandingPage;
